import styled from "@emotion/styled";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";

import { Automation, Close } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, RadiusToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import { config } from "config/config";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { openInNewCard } from "helpers/routing";
import { useAgentFlag } from "hooks/use-agent-flag";
import { useBreakpoints } from "hooks/use-breakpoints";
import { useMacros } from "hooks/use-macros";
import { AutomationsRoutes } from "routes/automations/constants";
import { selectIsViewer } from "store/entities/agents";
import { selectFeatureToggleMacros } from "store/entities/license";
import { withVar } from "themes/helpers";

export const MacrosPromoBanner: FC = () => {
  const inIframe = isInIframe();
  const { isMobile } = useBreakpoints();
  const history = useHistory();
  const { privateMacros, sharedMacros, isLoading: fetchingMacros } = useMacros();
  const [bannerAlreadyClosed, setBannerAsClosed] = useAgentFlag("topMessageBoxMacrosPromoBannerClosed");
  const [noMacrosCreatedUntilOpenedBanner, setNoMacrosCreatedUntilOpenedBanner] = useState(false); // keep banner opened even if other user creates a macro meanwhile

  const isViewer = useSelector(selectIsViewer);
  const marcosAreEnabled = useSelector(selectFeatureToggleMacros);

  useEffect(() => {
    if (!fetchingMacros) {
      setNoMacrosCreatedUntilOpenedBanner(privateMacros.length === 0 && sharedMacros.length === 0);
    }
  }, [fetchingMacros]);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleClose = (eventPlace: "CTA" | "Close") => {
    setBannerAsClosed(true);
    trackEngagement("Macros promotion: ticket details banner on top of the message box", EventPlace.Tickets, {
      clickedAction: eventPlace,
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTryMacroClick = () => {
    if (inIframe) {
      openInNewCard(`${config.appUrl}${AutomationsRoutes.Macros}`);
    } else {
      history.push(AutomationsRoutes.Macros);
    }
    handleClose("CTA");
  };

  if (
    isViewer ||
    bannerAlreadyClosed ||
    !marcosAreEnabled ||
    fetchingMacros ||
    !noMacrosCreatedUntilOpenedBanner ||
    isMobile
  )
    return null;

  return (
    <Wrapper>
      <Icon size="small" source={Automation} />
      <Text customColor={withVar(DesignToken.ContentBasicSecondary)} size="sm">
        Save time and complete a set of actions in tickets with just one click 👉
        <ActionButton kind="link" size="compact" onClick={handleTryMacroClick}>
          <Text size="sm">Try Macros</Text>
        </ActionButton>
      </Text>
      <CloseButton
        kind="plain"
        onClick={() => handleClose("Close")}
        size="xcompact"
        icon={<Icon size="small" source={Close} />}
        aria-label="close-macros-promo-banner"
        data-testid="close-macros-promo-banner"
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing2)};

  background: ${withVar(DesignToken.SurfaceSecondaryDefault)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  padding: ${withVar(SpacingToken.Spacing1)} ${withVar(SpacingToken.Spacing3)};
  margin-bottom: ${withVar(SpacingToken.Spacing2)};
`;

const ActionButton = styled(Button)`
  margin-left: ${withVar(SpacingToken.Spacing1)};
`;

const CloseButton = styled(Button)`
  margin-left: auto;
`;
